import React from 'react';
import { Avatar } from "@progress/kendo-react-layout";
import {
  AuthService,
  Component,
  COOKIE,
  CookieUtil,
  DynamicStyleManager,
  PuzzleButton,
  Container,
  LocalCache,
  VBox,
  Position,
  Paths,
  HBox,
  Dispatcher
} from "puzzle";
import { get } from 'lodash';
import { PREFERENCES_DATA, THEMES } from "../Preferences/Types";
import GlobalInfoProvider from "../util/GlobalInfo";
import { isNull } from "util";
import ImpersonateUserPicker from "./Impersonate/ImpersonateUserPicker";
import { ImpersonateContext } from "./Impersonate/Context/ImpersonateContext";
import { ViewContext } from './ViewOption/Context/ViewProvider';
import Policies from "../util/Policies";
import { DropDownList, ListItemProps } from "@progress/kendo-react-dropdowns";
import ChangeLanguage from "./ChangeLanguage";

type HEADER_PROPS = {
  appVersion: string,
  config: {
    userActions: Paths[]
  },
  isLoggingApp: boolean,
  isSwitch: boolean,
  projectLogoText?: string
}
type HEADER_STATE = {
  displayName: string,
  theme: string,
  themes: {} | THEMES,
  userName: string
}

class Header extends Component<HEADER_PROPS, HEADER_STATE> {
  headerSwitcherRoutes: string[] = [
    'view/dashboard',
    'view/submissionList',
    'view/templates',
    'view/taskList'
  ];
  mediaQuery = window.matchMedia('(max-width: 767px)');
  disableThemeSwitch: boolean = false;
  realUsername: string = '';
  submissionListPath = '/view/submissionList';

  public state: HEADER_STATE = {
    displayName: '',
    theme: '',
    themes: {},
    userName: ''
  };

  /**
   * @inheritDoc
   */
  moduleDidMount = () => {
    this.initialData();
    this.handleUserNameOnResponsiveness();
    window.addEventListener('resize', this.handleUserNameOnResponsiveness);
  };

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleUserNameOnResponsiveness);
  }

  /**
   * About dropdown item render
   */
  aboutRenderer = () => {
    return <Container
      justifyContentCenter
      displayFlex
      className={'tp-100'}
      flexDirectionColumn
      gap={2}
    >
      {[{
        onClick: this.openAbout,
        icon: 'tp-icon tp-info',
        text: this.translate('userMenu.about')
      }].map(this.getNavLinks)}
    </Container>
  }

  /**
   * Change theme is failed, set theme change button to enabled again
   */
  changeThemeFailed = () => {
    this.disableThemeSwitch = false;
    this.forceUpdate();
  }

  /**
   * Render header section in user dropdown list popup
   */
  dropDownHeader = () => {
    const { displayName } = this.state;
    const userEmailAddress = Dispatcher.appOperationData.initializeUserData?.userProfile?.userInfo.emailAddress;
    return (
      <VBox alignItemsCenter gap={1}>
        <Avatar
          className={'tp-avatar'}
          key={'impersonateAvatar'}
        >
          <span aria-hidden={"true"}>{displayName?.charAt(0).toUpperCase()}</span>
        </Avatar>
        <span
          className={'tp-text-medium tp-font-size-md'}
          key={'impersonateUsrNameValue'}
        >
          {displayName}
        </span>
        <span className={'tp-font-size-sm'}>{userEmailAddress}</span>
      </VBox>
    )
  }

  /**
   * returns single dropdown item configuration
   *
   * @param item
   * @param index
   */
  getNavLinks = (item: any, index: number) => {
    const path = item.path;
    return <HBox
      alignItemsCenter
      key={`pd-main-sidebar-link-${index}`}
      onClick={(event: any) => item.path ? this.navLinkOnClick(event, path) : item.onClick.call(this)}
      gap={2}
    >
      <i aria-hidden={true} className={`tp-icon tp-${item.icon} tp-sidebar-toolbar-item-link-icon`}/>
      <span>{item.text}</span>
    </HBox>
  };

  /**
   * Handler for Privacy Policy
   */
  handlePrivacyPolicy = () => {
    window.open('https://www.transperfect.com/about/data-privacy-client-tech-user', '_blank')
  };

  /**
   * Handler for switching app to Aqua
   */
  handleSwitchToAqua = (leaveAction = 'switchToAqua') => {
    const pathname = document.location.pathname;
    if (pathname.includes(this.submissionListPath)) {
      this.dispatch('checkForDrawerChangesBeforeRedirect', '', { leaveAction });
    } else {
      this.dispatch('checkForChangesBeforeRedirect', '', { leaveAction });
    }
  };

  /**
   * Handler for handling the userName on specific media query
   * It will replace the full name of user into first letter only bellow 768px
   */
  handleUserNameOnResponsiveness = () => {
    let displayName = this.mediaQuery.matches ? this.realUsername?.charAt(0).toUpperCase() : this.realUsername;

    this.setState({
      displayName
    });
  }

  /**
   * Renderer for userSettings item
   * @param li
   * @param itemProps
   */
  itemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps): React.ReactNode => {
    let itemChildren: any;
    switch (itemProps.dataItem) {
      case 'about':
        itemChildren = this.aboutRenderer();
        break;
      case 'logout':
        itemChildren = this.logoutSectionRender();
        break;
      case 'switchApp':
        itemChildren = this.switchAppSectionRender();
        break;
      case 'privacyPolicy':
        itemChildren = this.privacyPolicyRender();
        break;
      case 'preferences':
        itemChildren = this.preferencesRender();
        break;
      default:
        break;
    }

    return React.cloneElement(li, {
      ...li.props,
      className: `${li.props.className} tp-list-item-with-icon`
    }, itemChildren);
  }

  /**
   * Logout dropdown item render
   */
  logoutSectionRender = (): React.ReactElement | null => {
    return <Container
      justifyContentCenter
      displayFlex
      className={'tp-100'}
      flexDirectionColumn
      gap={2}
    >
      {[{
        onClick: () => this.dispatch('userLogout'),
        icon: 'k-icon k-i-userLogout',
        text: this.translate('userMenu.logout')
      }].map(this.getNavLinks)}
    </Container>
  };

  /**
   * Added to support preventing route change if needed
   * @param event
   * @param path to be redirected to
   */
  navLinkOnClick = (event: React.MouseEvent, path: string) => {
    const pathname = document.location.pathname;
    if (pathname !== path) {
      //clears persistent filter from dispatcher
      this.dispatch('clearFilterMainGrid');
      //TODO need smarter solution for condition bellow
      //paths that should return true are ['/submission', '/submission/:id', '/template', '/template/:id']
      const isFromSubmissionFolder = pathname.includes('/view/submission/') || pathname.includes('/view/template/') || pathname.endsWith('/view/submission') || pathname.endsWith('/view/template');
      if (isFromSubmissionFolder) {
        event.preventDefault();
        if (path !== '/view/submission') {
          this.dispatch('checkForChangesBeforeRedirect', Position.start, { path, leaveAction: 'redirect' });
        }
      } else if (pathname.includes(this.submissionListPath) && path !== this.submissionListPath) {
        event.preventDefault();
        this.dispatch('checkForDrawerChangesBeforeRedirect', Position.start, { path });
      } else if (pathname.includes('/view/preferences')) {
        this.dispatch('checkForPreferencesBeforeRedirect', {
          pos: Position.start,
          persistent: ['path']
        }, { path });
      } else {
        this.dispatch('reloadPathPD', '', { path })
      }
    }
  };

  /**
   * Open About dialog on click
   */
  openAbout = () => {
    this.dispatch('openAbout', '', {
      appName: this.translate('appName'),
      appVersion: this.props.appVersion
    });
  };

  /**
   * Preferences dropdown item render
   */
  preferencesRender = (): React.ReactElement[] => {
    const { userActions } = this.props.config;
    let preferences: any = {};
    preferences = [
      ...userActions
    ];
    return preferences.map(this.getNavLinks);
  };

  /**
   * Save current selected theme to cookie and resets styles
   *
   * @param theme
   */
  saveThemeDataToCookie = (theme: string): void => {
    DynamicStyleManager.emptyStyles();
  };

  /**
   * Sets `state`s `userName` value.
   *
   * @param userName
   * @param preferences
   * @param themes
   */
  setUserData = (userName: string, preferences: PREFERENCES_DATA, themes: THEMES): void => {
    // realUsername must be saved because if displayName changes to be only one character due to lower screen size the value of it will be lost.
    this.realUsername = this.props.isSwitch ? LocalCache.getItem('impersonated', 'sessionStorage') : userName;
    const displayName = this.mediaQuery.matches ? this.realUsername.charAt(0).toUpperCase() : this.realUsername;
    GlobalInfoProvider.setTheme(preferences?.theme);
    this.setState({
      userName,
      theme: preferences.theme,
      themes,
      displayName
    });
  };

  /**
   * Sets theme to header
   * @param theme
   */
  setTheme = (theme: string) => {
    GlobalInfoProvider.setTheme(theme);
    this.setState({
      theme
    }, () => {
      this.disableThemeSwitch = false;
      this.forceUpdate();
    });
  };

  /**
   * If theme from cookie is different form theme that server sends, sets new theme to cookie and reloads theme.
   *
   * @param preferences
   */
  shouldThemeReload = (preferences: PREFERENCES_DATA): boolean => {
    const cookieTheme = CookieUtil.get(COOKIE.THEME)?.split('.')[0];
    let serverTheme = preferences.theme?.split('.')[0];
    let theme = cookieTheme === serverTheme;

    if (!theme) {
      this.saveThemeDataToCookie(serverTheme);
    }
    return !theme;
  };

  shouldComponentUpdate(props: any, state: any) {
    return state.userName !== this.translate('userMenu.defaultName');
  }

  /**
   * Switches current theme and stores selection on server
   */
  toggleTheme = (): void => {
    if (!this.disableThemeSwitch) {
      this.disableThemeSwitch = true;
      this.forceUpdate();
      let { theme, themes } = this.state;
      const keys = Object.keys(themes);

      const hasFunctionalCookie: boolean = CookieUtil.hasFunctionalCookie();
      const hasConsentUrl: boolean = CookieUtil.hasConsentUrl();
      let allowChange = hasConsentUrl ? hasFunctionalCookie : true;
      if (allowChange) {
        // @ts-ignore
        theme = isNull(theme) || theme === themes[keys[0]].name ? themes[keys[1]].name : themes[keys[0]].name;
      }

      this.setState({ theme }, () => {
        const { theme } = this.state;
        let changeThemePath = `/theme-provider/${theme}`;
        this.saveThemeDataToCookie(theme);

        this.dispatch('switchTheme', '', {
          path: changeThemePath,
          newTheme: this.state.theme,
          themeCookieName: COOKIE.THEME,
          theme
        });
      });
    }
  };

  initialData(isAuthenticated: Promise<boolean> = AuthService.isAuthenticated()) {
    const userMenuLocale = this.translate('userMenu') as any;
    this.setState({
      userName: isAuthenticated ? this.state.userName : userMenuLocale.defaultName
    });
  };

  /**
   * Open help center handler
   * @param initializeUserData
   * @param configData
   */
  openHelpPage = (initializeUserData: any, configData: any) => {
    const locale = get(initializeUserData, 'preferences.language.locale');
    const helpUserProfile = get(initializeUserData, 'userProfile.userInfo');
    let helpLinks = get(configData, 'helpLinks');
    let startOfHelpLink = get(initializeUserData, 'helpcenter.address')
    let helpUrl = '';
    const { subtype, userType } = helpUserProfile;
    const subTypeLocale = helpLinks[subtype];
    const userTypeLocale = helpLinks[userType];
    if (subTypeLocale) {
      helpUrl = this.getUrlByLocale(subTypeLocale, locale);
    } else if (userTypeLocale) {
      helpUrl = this.getUrlByLocale(userTypeLocale, locale);
    }

    if (helpUrl) {
      window.open(helpUrl.replace('{0}', startOfHelpLink), 'helpURL');
    }
  };

  /**
   * Open GL Home handler
   * @param initializeUserData
   */
  openGLHome = (initializeUserData: { applicationInfo: { glHomeAddress: string } }) => {
    const GLHomeUrl = get(initializeUserData, 'applicationInfo.glHomeAddress');
    if (GLHomeUrl) {
      window.open(GLHomeUrl, "_blank");
    }
  };

  /**
   * Returns help url for given locale
   * if locale doesn't exist returns default url
   */
  getUrlByLocale = (typeLocale: any, locale: string) => locale && typeLocale[locale] ? typeLocale[locale] : typeLocale['default_url'];

  /**
   * Privacy Policy dropdown item render
   */
  privacyPolicyRender = (): React.ReactElement | null => {
    return <Container
      justifyContentCenter
      displayFlex
      className={'tp-100'}
      flexDirectionColumn
      gap={2}
    >
      {[{
        onClick: this.handlePrivacyPolicy,
        icon: 'shield',
        text: this.translate('userMenu.privacyPolicy')
      }].map(this.getNavLinks)}
    </Container>
  };

  /**
   * Switch App dropdown item render
   */
  switchAppSectionRender = (): React.ReactElement | null => {
    return <Container
      justifyContentCenter
      displayFlex
      className={'tp-100'}
      flexDirectionColumn
      gap={2}
    >
      {[{
        onClick: this.handleSwitchToAqua,
        icon: 'switch-app',
        text: this.translate('userMenu.switchApp')
      }].map(this.getNavLinks)}
    </Container>
  };

  /**
   * User dropdown list renderer
   */
  userSettingsRender = (value: any) => {
    const isSsoUser = Dispatcher.appOperationData.initializeUserData?.userProfile?.userInfo.isSsoUser;
    const hasSwitchInterfacePolicy = Policies.hasSwitchInterfacePolicy;
    const data: any[] = ['preferences','privacyPolicy', 'about', 'logout'];

    if(isSsoUser && hasSwitchInterfacePolicy && !value.isSwitch) {
      data.unshift('switchApp')
    }

    return <DropDownList
      className={'tp-header-user-dropdown'}
      popupSettings={{ className: 'tp-header-user-dropdown-list' }}
      data={data}
      header={this.dropDownHeader()}
      itemRender={this.itemRender.bind(this)}
      valueRender={this.valueRender}
    />
  };

  /**
   * Value render
   * @param element
   */
  valueRender = (element: React.ReactElement<HTMLSpanElement>): React.ReactNode => {
    const {
      displayName
    } = this.state;

    const children = [
      <Avatar
        className={'tp-avatar'}
        key={'impersonateAvatar'}
      >
        <span aria-hidden={"true"}>{displayName?.charAt(0).toUpperCase()}</span>
      </Avatar>,
      <span
        key={'usrNameValue'}
      >
        {displayName}
      </span>
    ];

    return React.cloneElement(element, { ...element.props }, children);
  };

  renderBody() {
    const { theme, displayName} = this.state;
    const isDarkTheme: boolean = !!(theme && theme.length && theme.includes('dark'));
    const themeBtnClass = isDarkTheme ? 'tp-icon tp-light-mode' : 'tp-icon tp-dark-mode';
    const themeBtnLabel = isDarkTheme ? this.translate('SRL-switchLightModeBtn') : this.translate('SRL-switchDarkModeBtn');
    const { pathname } = window.location;
    const visibleSwitch = this.headerSwitcherRoutes.some((item: string) => pathname.includes(item));
    return (
      <Container
        hBox
        alignItemsCenter
        justifyContentBetween
        className="tp-viewport-header tp-100"
      >
        <Container className={'tp-header-logo-wrap'} />
        <Container
          gap={2}
          hBox
          alignItemsCenter
        >
          <>
            {visibleSwitch &&
              <>
                <ChangeLanguage />
                <ViewContext.Consumer>
                  {({ viewType, setViewType }) => {
                    const ariaLabelText = viewType === 'COMFORTABLE' ? this.translate('SRL-switchComfortableViewBtn') : this.translate('SRL-switchCompactViewBtn')
                    const iconClass = viewType === 'COMFORTABLE' ? 'tp-icon tp-compact' : 'tp-icon tp-comfortable';
                    return (
                      <PuzzleButton
                        aria-label={ariaLabelText}
                        titleText={ariaLabelText}
                        onClick={() => {
                          const newViewType = viewType === "COMFORTABLE" ? "COMPACT" : "COMFORTABLE";
                          setViewType(newViewType);
                          this.dispatch('switchView', '', { newViewType })
                        }}
                        className={'tp-theme-button'}
                        iconClass={iconClass}
                        fillMode={'flat'}
                        tooltipPosition={'bottom'}
                        withTooltip
                      />
                    )
                  }}
                </ViewContext.Consumer>
                <PuzzleButton
                  aria-label={themeBtnLabel}
                  titleText={themeBtnLabel}
                  disabled={this.disableThemeSwitch}
                  onClick={this.toggleTheme}
                  className={'tp-theme-button'}
                  rounded={'full'}
                  iconClass={themeBtnClass}
                  fillMode={'flat'}
                  tooltipPosition={'bottom'}
                  withTooltip
                />
              </>
            }
            {(visibleSwitch || pathname.includes('view/preferences')) && <ImpersonateContext.Consumer>
              {value => (
                <>
                  {
                    ((!value.isSwitch && value.canSwitchUser) &&
                      <ImpersonateUserPicker userName={displayName} />
                    )
                  }
                </>
              )
              }
            </ImpersonateContext.Consumer>}
            {AuthService.isAuthenticated() &&
              <PuzzleButton
                aria-label={this.translate('userMenu.helpCenter')}
                titleText={this.translate('userMenu.helpCenter')}
                onClick={() => this.dispatch('openHelpCenter')}
                className={'tp-theme-button tp-help-button'}
                rounded={'full'}
                iconClass={'tp-icon tp-help'}
                fillMode={'flat'}
                tooltipPosition={'bottom'}
                withTooltip
              />
            }{
            <PuzzleButton
              aria-label={this.translate('userMenu.GLHome')}
              titleText={this.translate('userMenu.GLHome')}
              onClick={() => this.dispatch('openGLHome')}
              className={'tp-theme-button tp-glhome-button'}
              rounded={'full'}
              iconClass={'tp-icon tp-home'}
              fillMode={'flat'}
              tooltipPosition={'bottom'}
              withTooltip
            />
          }
          </>
          <>
            <ImpersonateContext.Consumer>
              {value => (
                <>
                  {this.userSettingsRender(value)}
                </>
              )
              }
            </ImpersonateContext.Consumer>
          </>
        </Container>
      </Container>

    )
  }
}

export default Header;
